/**
 * Alpine.js Undo/Redo Manager Plugin
 *
 * Manages an undo stack and redo stack for builder state changes.
 * Records state changes for: section add, delete, reorder, content update, design change.
 * Dispatches Livewire calls to restore state and triggers preview-refresh events.
 *
 * Stack entry structure:
 * {
 *     id: 'uuid',
 *     timestamp: Date.now(),
 *     type: 'section_update' | 'section_add' | 'section_delete' | 'section_reorder' | 'design_change',
 *     payload: {
 *         sectionUuid: '...',
 *         previousState: { content: {...}, settings: {...}, items: [...] },
 *         newState: { content: {...}, settings: {...}, items: [...] },
 *     }
 * }
 *
 * Requirements: 19.1, 19.2, 19.3, 19.4, 19.6
 */
export default function undoRedoPlugin(Alpine) {
    // Singleton guard — prevent multiple engine instances
    if (window.__undoRedoManagerInitialized) return;
    window.__undoRedoManagerInitialized = true;

    const MAX_STACK_SIZE = 50;

    /**
     * Generate a UUID v4
     */
    function generateUuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Deep clone an object to prevent reference issues
     */
    function deepClone(obj) {
        if (obj === null || typeof obj !== 'object') return obj;
        try {
            return JSON.parse(JSON.stringify(obj));
        } catch (e) {
            return obj;
        }
    }

    class UndoRedoManager {
        constructor() {
            this.undoStack = [];
            this.redoStack = [];
            this.isRestoring = false; // Flag to prevent recording during restore
            this.listeners = [];
        }

        /**
         * Check if undo is available
         * @returns {boolean}
         */
        canUndo() {
            return this.undoStack.length > 0;
        }

        /**
         * Check if redo is available
         * @returns {boolean}
         */
        canRedo() {
            return this.redoStack.length > 0;
        }

        /**
         * Get the count of items in the undo stack
         * @returns {number}
         */
        get undoCount() {
            return this.undoStack.length;
        }

        /**
         * Get the count of items in the redo stack
         * @returns {number}
         */
        get redoCount() {
            return this.redoStack.length;
        }

        /**
         * Record a new state change.
         * Clears the redo stack when a new action is performed after undo.
         * Evicts oldest entries when stack exceeds 50 (FIFO from bottom).
         *
         * @param {string} type - The type of change: 'section_update', 'section_add', 'section_delete', 'section_reorder', 'design_change'
         * @param {object} payload - The payload containing previousState and newState
         */
        record(type, payload) {
            // Don't record if we're currently restoring state
            if (this.isRestoring) return;

            const entry = {
                id: generateUuid(),
                timestamp: Date.now(),
                type,
                payload: deepClone(payload),
            };

            // Clear redo stack when a new action is performed
            this.redoStack = [];

            // Add to undo stack
            this.undoStack.push(entry);

            // Evict oldest entries when stack exceeds MAX_STACK_SIZE (FIFO from bottom)
            while (this.undoStack.length > MAX_STACK_SIZE) {
                this.undoStack.shift();
            }

            this._notifyListeners();
        }

        /**
         * Undo the most recent change.
         * Pops from undo stack, pushes to redo stack, restores previous state.
         *
         * @param {function} restoreCallback - Callback to restore state via Livewire
         * @returns {object|null} The entry that was undone, or null if stack is empty
         */
        undo(restoreCallback) {
            if (!this.canUndo()) return null;

            const entry = this.undoStack.pop();
            this.redoStack.push(entry);

            // Set flag to prevent recording during restore
            this.isRestoring = true;

            try {
                if (typeof restoreCallback === 'function') {
                    restoreCallback(entry.type, entry.payload.previousState, entry.payload);
                }
            } finally {
                this.isRestoring = false;
            }

            this._notifyListeners();
            return entry;
        }

        /**
         * Redo the most recently undone change.
         * Pops from redo stack, pushes to undo stack, restores new state.
         *
         * @param {function} restoreCallback - Callback to restore state via Livewire
         * @returns {object|null} The entry that was redone, or null if stack is empty
         */
        redo(restoreCallback) {
            if (!this.canRedo()) return null;

            const entry = this.redoStack.pop();
            this.undoStack.push(entry);

            // Set flag to prevent recording during restore
            this.isRestoring = true;

            try {
                if (typeof restoreCallback === 'function') {
                    restoreCallback(entry.type, entry.payload.newState, entry.payload);
                }
            } finally {
                this.isRestoring = false;
            }

            this._notifyListeners();
            return entry;
        }

        /**
         * Clear both stacks
         */
        clear() {
            this.undoStack = [];
            this.redoStack = [];
            this._notifyListeners();
        }

        /**
         * Add a listener for stack changes
         * @param {function} callback
         */
        addListener(callback) {
            if (typeof callback === 'function') {
                this.listeners.push(callback);
            }
        }

        /**
         * Remove a listener
         * @param {function} callback
         */
        removeListener(callback) {
            this.listeners = this.listeners.filter(fn => fn !== callback);
        }

        /**
         * Notify all listeners of stack changes
         * @private
         */
        _notifyListeners() {
            const state = {
                canUndo: this.canUndo(),
                canRedo: this.canRedo(),
                undoCount: this.undoCount,
                redoCount: this.redoCount,
            };

            this.listeners.forEach(fn => {
                try {
                    fn(state);
                } catch (e) {
                    console.error('[UndoRedoManager] Listener error:', e);
                }
            });
        }

        /**
         * Get a description of the most recent undo action (for UI hints)
         * @returns {string|null}
         */
        getUndoDescription() {
            if (!this.canUndo()) return null;
            const entry = this.undoStack[this.undoStack.length - 1];
            return this._getActionDescription(entry);
        }

        /**
         * Get a description of the most recent redo action (for UI hints)
         * @returns {string|null}
         */
        getRedoDescription() {
            if (!this.canRedo()) return null;
            const entry = this.redoStack[this.redoStack.length - 1];
            return this._getActionDescription(entry);
        }

        /**
         * Convert an entry type to a human-readable description
         * @private
         */
        _getActionDescription(entry) {
            if (!entry) return null;

            const descriptions = {
                'section_add': 'Add section',
                'section_delete': 'Delete section',
                'section_reorder': 'Reorder sections',
                'section_update': 'Edit section',
                'design_change': 'Design change',
            };

            return descriptions[entry.type] || 'Change';
        }
    }

    // Manager instance — lazily created
    let manager = null;

    function getManager() {
        if (!manager) {
            manager = new UndoRedoManager();
        }
        return manager;
    }

    // Register Alpine magic helper
    Alpine.magic('undoRedo', () => {
        const mgr = getManager();
        return {
            /**
             * Record a state change
             * @param {string} type - Change type
             * @param {object} payload - { sectionUuid?, previousState, newState }
             */
            record: (type, payload) => mgr.record(type, payload),

            /**
             * Undo the most recent change
             * @param {function} restoreCallback - Called with (type, stateToRestore, fullPayload)
             */
            undo: (restoreCallback) => mgr.undo(restoreCallback),

            /**
             * Redo the most recently undone change
             * @param {function} restoreCallback - Called with (type, stateToRestore, fullPayload)
             */
            redo: (restoreCallback) => mgr.redo(restoreCallback),

            /**
             * Check if undo is available
             */
            canUndo: () => mgr.canUndo(),

            /**
             * Check if redo is available
             */
            canRedo: () => mgr.canRedo(),

            /**
             * Get the number of undo actions available
             */
            get undoCount() {
                return mgr.undoCount;
            },

            /**
             * Get the number of redo actions available
             */
            get redoCount() {
                return mgr.redoCount;
            },

            /**
             * Clear both undo and redo stacks
             */
            clear: () => mgr.clear(),

            /**
             * Add a listener for stack state changes
             * @param {function} callback - Called with { canUndo, canRedo, undoCount, redoCount }
             */
            addListener: (callback) => mgr.addListener(callback),

            /**
             * Remove a listener
             */
            removeListener: (callback) => mgr.removeListener(callback),

            /**
             * Get description of the next undo action
             */
            getUndoDescription: () => mgr.getUndoDescription(),

            /**
             * Get description of the next redo action
             */
            getRedoDescription: () => mgr.getRedoDescription(),
        };
    });

    // Register Alpine data component for builder integration
    Alpine.data('undoRedoManager', () => ({
        canUndo: false,
        canRedo: false,
        undoCount: 0,
        redoCount: 0,

        init() {
            const mgr = getManager();

            // Update local state from manager
            const updateState = (state) => {
                this.canUndo = state.canUndo;
                this.canRedo = state.canRedo;
                this.undoCount = state.undoCount;
                this.redoCount = state.redoCount;
            };

            // Initial state
            updateState({
                canUndo: mgr.canUndo(),
                canRedo: mgr.canRedo(),
                undoCount: mgr.undoCount,
                redoCount: mgr.redoCount,
            });

            // Listen for changes
            mgr.addListener(updateState);

            // Listen for builder events to record state changes
            this._setupEventListeners();
        },

        /**
         * Record a state change
         * @param {string} type - Change type
         * @param {object} payload - { sectionUuid?, previousState, newState }
         */
        record(type, payload) {
            const mgr = getManager();
            mgr.record(type, payload);
        },

        /**
         * Perform undo operation
         */
        performUndo() {
            if (!this.canUndo) return;

            const mgr = getManager();
            mgr.undo((type, stateToRestore, fullPayload) => {
                this._restoreState(type, stateToRestore, fullPayload);
            });
        },

        /**
         * Perform redo operation
         */
        performRedo() {
            if (!this.canRedo) return;

            const mgr = getManager();
            mgr.redo((type, stateToRestore, fullPayload) => {
                this._restoreState(type, stateToRestore, fullPayload);
            });
        },

        /**
         * Restore state via Livewire call
         * @private
         */
        _restoreState(type, stateToRestore, fullPayload) {
            // Dispatch to Livewire to persist the restored state
            // The specific handling depends on the change type
            switch (type) {
                case 'section_add':
                    // Undo of add = delete the section
                    // Redo of add = re-create the section
                    if (stateToRestore === null) {
                        // Undo: delete the added section
                        this._dispatchLivewire('undoSectionAdd', {
                            sectionUuid: fullPayload.sectionUuid,
                        });
                    } else {
                        // Redo: re-create the section
                        this._dispatchLivewire('redoSectionAdd', {
                            sectionData: stateToRestore,
                        });
                    }
                    break;

                case 'section_delete':
                    // Undo of delete = restore the section
                    // Redo of delete = delete again
                    if (stateToRestore !== null) {
                        // Undo: restore the deleted section
                        this._dispatchLivewire('undoSectionDelete', {
                            sectionData: stateToRestore,
                        });
                    } else {
                        // Redo: delete the section again
                        this._dispatchLivewire('redoSectionDelete', {
                            sectionUuid: fullPayload.sectionUuid,
                        });
                    }
                    break;

                case 'section_reorder':
                    // Restore the position order
                    this._dispatchLivewire('undoSectionReorder', {
                        orderedIds: stateToRestore.orderedIds,
                    });
                    break;

                case 'section_update':
                    // Restore section content/settings
                    this._dispatchLivewire('undoSectionUpdate', {
                        sectionUuid: fullPayload.sectionUuid,
                        content: stateToRestore.content,
                        settings: stateToRestore.settings,
                        sectionSettings: stateToRestore.sectionSettings,
                        items: stateToRestore.items,
                    });
                    break;

                case 'design_change':
                    // Restore project design settings
                    this._dispatchLivewire('undoDesignChange', {
                        designData: stateToRestore,
                    });
                    break;

                default:
                    console.warn('[UndoRedoManager] Unknown change type:', type);
            }

            // Dispatch preview-refresh event
            window.dispatchEvent(new CustomEvent('preview-refresh'));
            
            // Also dispatch Livewire event for preview refresh
            if (window.Livewire) {
                window.Livewire.dispatch('preview-refresh');
            }
        },

        /**
         * Dispatch a Livewire event
         * @private
         */
        _dispatchLivewire(eventName, payload) {
            if (window.Livewire) {
                window.Livewire.dispatch(eventName, payload);
            }
        },

        /**
         * Setup event listeners for builder state changes
         * @private
         */
        _setupEventListeners() {
            // Listen for keyboard shortcut events from editor component
            // These are dispatched when user presses Ctrl+Z / Ctrl+Shift+Z
            window.addEventListener('builder-undo-shortcut', () => {
                this.performUndo();
            });

            window.addEventListener('builder-redo-shortcut', () => {
                this.performRedo();
            });

            // Listen for custom events from builder components
            // These events should include previousState and newState in detail

            window.addEventListener('builder:section-add', (e) => {
                if (e.detail) {
                    this.record('section_add', {
                        sectionUuid: e.detail.sectionUuid,
                        previousState: null, // No previous state for add
                        newState: e.detail.sectionData,
                    });
                }
            });

            window.addEventListener('builder:section-delete', (e) => {
                if (e.detail) {
                    this.record('section_delete', {
                        sectionUuid: e.detail.sectionUuid,
                        previousState: e.detail.sectionData,
                        newState: null, // No new state for delete
                    });
                }
            });

            window.addEventListener('builder:section-reorder', (e) => {
                if (e.detail) {
                    this.record('section_reorder', {
                        previousState: { orderedIds: e.detail.previousOrder },
                        newState: { orderedIds: e.detail.newOrder },
                    });
                }
            });

            window.addEventListener('builder:section-update', (e) => {
                if (e.detail) {
                    this.record('section_update', {
                        sectionUuid: e.detail.sectionUuid,
                        previousState: e.detail.previousState,
                        newState: e.detail.newState,
                    });
                }
            });

            window.addEventListener('builder:design-change', (e) => {
                if (e.detail) {
                    this.record('design_change', {
                        previousState: e.detail.previousState,
                        newState: e.detail.newState,
                    });
                }
            });

            // Listen for Livewire events as well
            document.addEventListener('livewire:init', () => {
                if (window.Livewire) {
                    window.Livewire.on('builder:section-add', (payload) => {
                        const data = Array.isArray(payload) ? payload[0] : payload;
                        this.record('section_add', {
                            sectionUuid: data.sectionUuid,
                            previousState: null,
                            newState: data.sectionData,
                        });
                    });

                    window.Livewire.on('builder:section-delete', (payload) => {
                        const data = Array.isArray(payload) ? payload[0] : payload;
                        this.record('section_delete', {
                            sectionUuid: data.sectionUuid,
                            previousState: data.sectionData,
                            newState: null,
                        });
                    });

                    window.Livewire.on('builder:section-reorder', (payload) => {
                        const data = Array.isArray(payload) ? payload[0] : payload;
                        this.record('section_reorder', {
                            previousState: { orderedIds: data.previousOrder },
                            newState: { orderedIds: data.newOrder },
                        });
                    });

                    window.Livewire.on('builder:section-update', (payload) => {
                        const data = Array.isArray(payload) ? payload[0] : payload;
                        this.record('section_update', {
                            sectionUuid: data.sectionUuid,
                            previousState: data.previousState,
                            newState: data.newState,
                        });
                    });

                    window.Livewire.on('builder:design-change', (payload) => {
                        const data = Array.isArray(payload) ? payload[0] : payload;
                        this.record('design_change', {
                            previousState: data.previousState,
                            newState: data.newState,
                        });
                    });
                }
            });
        },
    }));
}
